
import { CameraIcon } from "@heroicons/react/24/outline";
import { IPart } from '../../models/Part';
import { useMemo, useState } from "react";
import LoadingImage from "../shared/LoadingImage";

type ResolvedPartImage = { thumbnail: string; full: string };

export default function PartImage({ part, width = "w-48" }: { part: IPart | null | undefined, width?: string }) {

  const image = useMemo((): ResolvedPartImage | null => {
    if (!part) return null;
    const imgs = part.images;
    const list = Array.isArray(imgs) ? imgs : [];
    const fromGallery =
      list.find((i) => i.isThumbnail === true) ?? (list.length > 0 ? list[0] : undefined);
    const thumbFromGallery = fromGallery?.thumbnailUrl ?? fromGallery?.url;
    if (thumbFromGallery) {
      const full = fromGallery?.url ?? fromGallery?.thumbnailUrl ?? thumbFromGallery;
      return { thumbnail: thumbFromGallery, full };
    }
    if (part.cdnThumbnail) {
      return { thumbnail: part.cdnThumbnail, full: part.cdnThumbnail };
    }
    if (part.ebayThumbnail) {
      return { thumbnail: part.ebayThumbnail, full: part.ebayThumbnail };
    }
    return null;
  }, [part, part?.images, part?.cdnThumbnail, part?.ebayThumbnail]);

  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [fullImageLoaded, setFullImageLoaded] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
    setFullImageLoaded(false);
  };
  const handleMouseLeave = () => {
    setHovered(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <span
        className="flex justify-center relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {image ? (
          <img
            className={`${width} h-auto object-contain`}
            src={image.thumbnail}
            alt=""
          />
        ) : (
          <CameraIcon className="w-14 text-gray-600" />
        )}
      </span>

      {/* Full-size hover image */}
      {hovered && image && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            top: mousePos.y + 10,
            left: mousePos.x + 10,
          }}
        >
          {!fullImageLoaded && (
            <div className="w-16 h-16">
              <LoadingImage  />
            </div>
          )}
          <img
            src={image.full}
            alt=""
            className={`max-w-2xl max-h-xl border border-gray-300 shadow-lg rounded-md transition-opacity duration-300 ${
              fullImageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setFullImageLoaded(true)}
          />
        </div>
      )}
    </>
  );
}